import constants from 'constants/index';

const { ARCHVISION_TOKEN, ARCHVISION_REFRESH_TOKEN } = constants.TOKEN_NAMES;

// Backup keys used to stash the admin's own session while impersonating a user, plus a flag
// holding the impersonated user's display info for the "return to admin" banner.
export const ADMIN_BACKUP_TOKEN = 'av_admin_token';
export const ADMIN_BACKUP_REFRESH = 'av_admin_refresh_token';
export const IMPERSONATION_FLAG = 'av_impersonating';

const displayName = user => (
    [ user?.first_name, user?.last_name ].filter(Boolean).join(' ') || user?.email || `User ${user?.id}`
);

/**
 * Returns the impersonated user info ({ id, name }) when an impersonation session is active,
 * otherwise null.
 */
export const getImpersonation = () => {
    try {
        const raw = localStorage.getItem(IMPERSONATION_FLAG);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};

export const isImpersonating = () => !!getImpersonation();

/**
 * Swaps the current session over to the target user's tokens and reloads as that user.
 * The admin's own tokens are stashed under backup keys so they can be restored later.
 *
 * @param {object} args
 * @param {Function} args.setCookie - react-cookie setter
 * @param {{access_token: string, refresh_token: string}} args.session - impersonation tokens
 * @param {object} args.user - the target user (for the banner label)
 */
export const startImpersonation = ({ setCookie, session, user }) => {
    const accessToken = session?.access_token;
    const refreshToken = session?.refresh_token;
    if (!accessToken) return;

    // Only stash the admin session the first time, so nested impersonation can't overwrite the
    // real admin token with an already-impersonated one.
    if (!isImpersonating()) {
        const adminToken = localStorage.getItem(ARCHVISION_TOKEN);
        const adminRefresh = localStorage.getItem(ARCHVISION_REFRESH_TOKEN);
        if (adminToken) localStorage.setItem(ADMIN_BACKUP_TOKEN, adminToken);
        if (adminRefresh) localStorage.setItem(ADMIN_BACKUP_REFRESH, adminRefresh);
    }

    setCookie(ARCHVISION_TOKEN, accessToken, { path: '/' });
    localStorage.setItem(ARCHVISION_TOKEN, accessToken);
    if (refreshToken) localStorage.setItem(ARCHVISION_REFRESH_TOKEN, refreshToken);

    localStorage.setItem(IMPERSONATION_FLAG, JSON.stringify({
        id: user?.id,
        name: displayName(user),
    }));

    window.location.assign('/');
};

/**
 * Restores the stashed admin session and returns to the admin users page.
 *
 * @param {object} args
 * @param {Function} args.setCookie - react-cookie setter
 */
export const stopImpersonation = ({ setCookie }) => {
    const adminToken = localStorage.getItem(ADMIN_BACKUP_TOKEN);
    const adminRefresh = localStorage.getItem(ADMIN_BACKUP_REFRESH);

    if (adminToken) {
        setCookie(ARCHVISION_TOKEN, adminToken, { path: '/' });
        localStorage.setItem(ARCHVISION_TOKEN, adminToken);
    }
    if (adminRefresh) {
        localStorage.setItem(ARCHVISION_REFRESH_TOKEN, adminRefresh);
    }

    localStorage.removeItem(ADMIN_BACKUP_TOKEN);
    localStorage.removeItem(ADMIN_BACKUP_REFRESH);
    localStorage.removeItem(IMPERSONATION_FLAG);

    window.location.assign('/archvision-admin/users');
};

/**
 * Clears any impersonation backup state without touching the active session. Call this on
 * logout so a stale admin token is never left behind in localStorage.
 */
export const clearImpersonationBackup = () => {
    localStorage.removeItem(ADMIN_BACKUP_TOKEN);
    localStorage.removeItem(ADMIN_BACKUP_REFRESH);
    localStorage.removeItem(IMPERSONATION_FLAG);
};
